// src/components/dashboard/CaseLoadReport.jsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';

// Icons
import FolderSpecialRoundedIcon from '@mui/icons-material/FolderSpecialRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';

const caseLoadRecords = [
  {
    id: 'CL-0914',
    client: 'Jamal Kamal',
    clientInitial: 'JK',
    status: 'Active Review',
    statusColor: { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' },
    familyPlan: 'Family Stabilization Tier-1',
    treatmentPlan: '09/14/2026',
    privacySignature: 'Digitally Verified',
    diagnosticExpiration: '11/30/2026',
    notes: 'Intake consultation finalized. Awaiting payer verification for diagnostic behavioral code expansion.',
    staffName: 'Ali Ammar, TruEvv',
    staffRole: 'Primary Clinician',
    primarySupervisor: 'ammarjaffri605@gmail.com',
    dateSupervised: '09/12/2026',
    insurances: [
      {
        payer: 'Blue Cross Blue Shield',
        id: 'BCBS-10298',
        typeOfProgram: 'Commercial PPO',
        authNumber: 'AUTH-882194',
        codes: ['97110', '97112'],
        startEndDate: '01/01/2026 - 12/31/2026',
        units: 60,
        unitsLeft: 24,
        contacts: '+1 (800) 555-0199',
      },
    ],
  },
  {
    id: 'CL-0822',
    client: 'Liam Alexander Vance',
    clientInitial: 'LV',
    status: 'Authorized',
    statusColor: { bg: '#ECFDF5', text: '#059669', border: '#A7F3D0' },
    familyPlan: 'Standard Managed Care FP-402',
    treatmentPlan: '08/22/2026',
    privacySignature: 'Signed (01/10/2026)',
    diagnosticExpiration: '11/15/2027',
    notes: 'Client exhibits positive adherence to weekly speech therapy. Requires renewed authorization for occupational visits.',
    staffName: 'Dr. Sarah Jenkins, MD',
    staffRole: 'Senior Pediatric Specialist',
    primarySupervisor: 's.jenkins@trucarehealth.org',
    dateSupervised: '09/01/2026',
    insurances: [
      {
        payer: 'Medicaid State Plan',
        id: 'MCD-990214',
        typeOfProgram: 'State Behavioral Aid',
        authNumber: 'AUTH-773412',
        codes: ['90834', 'H2019'],
        startEndDate: '03/01/2026 - 03/01/2027',
        units: 80,
        unitsLeft: 58,
        contacts: '+1 (877) 444-9021',
      },
    ],
  },
  {
    id: 'CL-0711',
    client: 'Elena Sofia Rossi',
    clientInitial: 'ER',
    status: 'Authorized',
    statusColor: { bg: '#ECFDF5', text: '#059669', border: '#A7F3D0' },
    familyPlan: 'Comprehensive Care Plan 2026',
    treatmentPlan: '07/11/2026',
    privacySignature: 'Signed (03/14/2026)',
    diagnosticExpiration: '05/30/2027',
    notes: 'Bilingual evaluation completed. Monitoring behavioral interventions and tracking cognitive assessment progress.',
    staffName: 'Marcus Sterling, LCSW',
    staffRole: 'Licensed Social Worker',
    primarySupervisor: 'm.sterling@clinicsuite.io',
    dateSupervised: '08/18/2026',
    insurances: [
      {
        payer: 'Aetna Health Inc',
        id: 'AET-772109',
        typeOfProgram: 'Individual Advantage',
        authNumber: 'AUTH-441029',
        codes: ['90837', '96116'],
        startEndDate: '03/01/2026 - 03/01/2027',
        units: 40,
        unitsLeft: 12,
        contacts: '+1 (888) 321-4567',
      },
    ],
  },
  {
    id: 'CL-0902',
    client: 'Noah William Dubois',
    clientInitial: 'ND',
    status: 'Pending Renewal',
    statusColor: { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
    familyPlan: 'Family Dependent Tier II',
    treatmentPlan: '09/02/2026',
    privacySignature: 'Signed (05/20/2026)',
    diagnosticExpiration: '10/01/2027',
    notes: 'Quarterly treatment plan reviewed with primary caregiver. Coordination of benefits finalized with secondary payer.',
    staffName: 'Rachel Green, PsyD',
    staffRole: 'Clinical Psychologist',
    primarySupervisor: 'rachel.green@behavioralcare.net',
    dateSupervised: '09/10/2026',
    insurances: [
      {
        payer: 'UnitedHealthcare',
        id: 'UHC-550183',
        typeOfProgram: 'Medicaid Managed Care',
        authNumber: 'AUTH-332091',
        codes: ['90791', 'H2019'],
        startEndDate: '05/01/2026 - 04/30/2027',
        units: 80,
        unitsLeft: 4,
        contacts: '+1 (877) 444-9021',
      },
    ],
  },
  {
    id: 'CL-0619',
    client: 'Astrid Maeve Lindholm',
    clientInitial: 'AL',
    status: 'Pending Intake',
    statusColor: { bg: '#F8FAFC', text: '#64748B', border: '#E2E8F0' },
    familyPlan: 'N/A',
    treatmentPlan: '06/19/2026',
    privacySignature: 'Portal Invite Sent',
    diagnosticExpiration: 'Pending Exam',
    notes: '',
    staffName: 'David K. Hoffman, BCBA',
    staffRole: 'Board Certified Analyst',
    primarySupervisor: 'd.hoffman@trucarehealth.org',
    dateSupervised: 'N/A',
    insurances: [],
  },
  {
    id: 'CL-0908',
    client: 'Mateo Hiroshi Tanaka',
    clientInitial: 'MT',
    status: 'Authorized',
    statusColor: { bg: '#ECFDF5', text: '#059669', border: '#A7F3D0' },
    familyPlan: 'Comprehensive Care Plan 2026',
    treatmentPlan: '09/08/2026',
    privacySignature: 'Signed (02/11/2026)',
    diagnosticExpiration: '12/31/2027',
    notes: 'Patient successfully transitioned from intensive intake to bi-weekly maintenance therapy. Telehealth sessions verified.',
    staffName: 'Hannah Abbott, MSW',
    staffRole: 'Family Counselor',
    primarySupervisor: 'hannah.a@clinicsuite.io',
    dateSupervised: '09/12/2026',
    insurances: [
      {
        payer: 'Cigna Behavioral Health',
        id: 'CIG-109283',
        typeOfProgram: 'Employer Choice EPO',
        authNumber: 'AUTH-990142',
        codes: ['90832', '90847'],
        startEndDate: '02/01/2026 - 01/31/2027',
        units: 50,
        unitsLeft: 35,
        contacts: '+1 (800) 244-6224',
      },
    ],
  },
];

const CaseLoadReport = () => {
  const [payer, setPayer] = useState('');
  const [staff, setStaff] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [primaryStaff, setPrimaryStaff] = useState(false);

  const handleSearch = () => {
    console.log({ payer, staff, includeInactive, primaryStaff });
  };

  const handleClear = () => {
    setPayer('');
    setStaff('');
    setIncludeInactive(false);
    setPrimaryStaff(false);
  };

  return (
    <Box sx={{ width: '100%', pb: 6, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      
      {/* 1. Header Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #0B2540 0%, #1E3A5F 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              boxShadow: '0 6px 16px rgba(11, 37, 64, 0.25)',
              flexShrink: 0,
            }}
          >
            <FolderSpecialRoundedIcon sx={{ fontSize: '1.6rem', color: '#38BDF8' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '1.35rem',
                fontWeight: 800,
                color: '#0B2540',
                letterSpacing: '-0.3px',
                lineHeight: 1.15,
              }}
            >
              Case Load Report
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontWeight: 500, mt: 0.3 }}>
              View staff caseloads, authorizations, and active client assignments
            </Typography>
          </Box>
        </Box>

        <Chip
          label="6 Total Assigned Cases"
          sx={{
            fontWeight: 700,
            fontSize: '0.78rem',
            backgroundColor: '#F1F5F9',
            color: '#0B2540',
            borderRadius: '8px',
            px: 1,
          }}
        />
      </Box>

      {/* 2. Upgraded Filter Box Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: '24px 30px' },
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 4px 20px -6px rgba(15, 23, 42, 0.05)',
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2.5 }}>
          <SearchRoundedIcon sx={{ fontSize: '1.35rem', color: '#0B2540' }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0B2540' }}>
            Filter Case Loads
          </Typography>
        </Box>

        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#0F172A', mb: 0.8 }}>
              Payer
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={payer}
                onChange={(e) => setPayer(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#94A3B8', fontSize: '0.84rem' }}>Select Payer(s)</span>;
                  }
                  return selected;
                }}
                sx={{
                  borderRadius: '10px',
                  backgroundColor: '#F8FAFC',
                  fontSize: '0.84rem',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0B2540' },
                }}
              >
                <MenuItem value="" disabled>Select Payer(s)</MenuItem>
                <MenuItem value="Blue Cross Blue Shield">Blue Cross Blue Shield</MenuItem>
                <MenuItem value="Medicaid State Plan">Medicaid State Plan</MenuItem>
                <MenuItem value="Aetna Health Inc">Aetna Health Inc</MenuItem>
                <MenuItem value="UnitedHealthcare">UnitedHealthcare</MenuItem>
                <MenuItem value="Cigna Behavioral Health">Cigna Behavioral Health</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#0F172A', mb: 0.8 }}>
              Staff
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={staff}
                onChange={(e) => setStaff(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#94A3B8', fontSize: '0.84rem' }}>Select...</span>;
                  }
                  return selected;
                }}
                sx={{
                  borderRadius: '10px',
                  backgroundColor: '#F8FAFC',
                  fontSize: '0.84rem',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0B2540' },
                }}
              >
                <MenuItem value="" disabled>Select...</MenuItem>
                <MenuItem value="Ali Ammar, TruEvv">Ali Ammar, TruEvv</MenuItem>
                <MenuItem value="Dr. Sarah Jenkins, MD">Dr. Sarah Jenkins, MD</MenuItem>
                <MenuItem value="Marcus Sterling, LCSW">Marcus Sterling, LCSW</MenuItem>
                <MenuItem value="Rachel Green, PsyD">Rachel Green, PsyD</MenuItem>
                <MenuItem value="David K. Hoffman, BCBA">David K. Hoffman, BCBA</MenuItem>
                <MenuItem value="Hannah Abbott, MSW">Hannah Abbott, MSW</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#0B2540' } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>Include inactive clients</Typography>}
              sx={{ m: 0, mb: '4px' }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={primaryStaff}
                  onChange={(e) => setPrimaryStaff(e.target.checked)}
                  sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#0B2540' } }}
                />
              }
              label={<Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>Primary Staff</Typography>}
              sx={{ m: 0, mb: '4px' }}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button
            onClick={handleSearch}
            variant="contained"
            disableElevation
            startIcon={<SearchRoundedIcon sx={{ fontSize: '1.1rem' }} />}
            sx={{
              backgroundColor: '#0B2540',
              color: '#FFFFFF',
              textTransform: 'uppercase',
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: '0.6px',
              borderRadius: '8px',
              px: 3,
              py: '8px',
              '&:hover': { backgroundColor: '#1E3A5F' },
            }}
          >
            Search
          </Button>

          <Button
            onClick={handleClear}
            variant="outlined"
            disableElevation
            startIcon={<CleaningServicesRoundedIcon sx={{ fontSize: '1.05rem' }} />}
            sx={{
              borderColor: '#E2E8F0',
              color: '#475569',
              textTransform: 'uppercase',
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: '0.6px',
              borderRadius: '8px',
              px: 3,
              py: '8px',
              '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' },
            }}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* =========================================================
          3. UPGRADED & UNIQUE 6 CLINICAL CASELOAD CARDS
         ========================================================= */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
        {caseLoadRecords.map((item, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #E2E8F0',
              borderLeft: '5px solid #0B2540',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 4px 18px -4px rgba(15, 23, 42, 0.05)',
              overflow: 'hidden',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.09)',
              },
            }}
          >
            {/* Top Bar Header with Client Badge */}
            <Box
              sx={{
                px: { xs: 2.5, md: 3.5 },
                py: 2,
                backgroundColor: '#F8FAFC',
                borderBottom: '1px solid #EEF2F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
                <Avatar
                  sx={{
                    bgcolor: '#0B2540',
                    color: '#38BDF8',
                    fontWeight: 700,
                    width: 42,
                    height: 42,
                    fontSize: '0.95rem',
                  }}
                >
                  {item.clientInitial}
                </Avatar>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0F172A' }}>
                      {item.client}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: '#94A3B8', fontWeight: 600 }}>
                      #{item.id}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 500 }}>
                    Assigned: {item.staffName}
                  </Typography>
                </Box>
              </Box>

              <Chip
                label={item.status}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  backgroundColor: item.statusColor.bg,
                  color: item.statusColor.text,
                  border: `1px solid ${item.statusColor.border}`,
                  borderRadius: '20px',
                  px: 0.5,
                }}
              />
            </Box>

            {/* Inner Content Area */}
            <Box sx={{ p: { xs: 2.5, md: '24px 30px' } }}>
              
              {/* Client Clinical Parameters Matrix */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1.6, borderRadius: '10px', backgroundColor: '#F8FAFC', border: '1px solid #EEF2F6' }}>
                    <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748B', fontWeight: 700, letterSpacing: '0.5px' }}>
                      Family Plan
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F172A', mt: 0.4 }}>
                      {item.familyPlan}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1.6, borderRadius: '10px', backgroundColor: '#F8FAFC', border: '1px solid #EEF2F6' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <EventAvailableRoundedIcon sx={{ fontSize: '0.9rem', color: '#2563EB' }} />
                      <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748B', fontWeight: 700, letterSpacing: '0.5px' }}>
                        Treatment Plan
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F172A', mt: 0.4 }}>
                      {item.treatmentPlan}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1.6, borderRadius: '10px', backgroundColor: '#F8FAFC', border: '1px solid #EEF2F6' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <VerifiedUserRoundedIcon sx={{ fontSize: '0.9rem', color: '#059669' }} />
                      <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748B', fontWeight: 700, letterSpacing: '0.5px' }}>
                        Privacy Signature
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F172A', mt: 0.4 }}>
                      {item.privacySignature}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1.6, borderRadius: '10px', backgroundColor: '#F8FAFC', border: '1px solid #EEF2F6' }}>
                    <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748B', fontWeight: 700, letterSpacing: '0.5px' }}>
                      Diagnostic Expiration
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: item.diagnosticExpiration === 'Pending Exam' ? '#D97706' : '#0F172A', mt: 0.4 }}>
                      {item.diagnosticExpiration}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Enhanced Insurances Table */}
              <TableContainer
                sx={{
                  borderRadius: '10px',
                  border: '1px solid #E2E8F0',
                  mb: 3,
                }}
              >
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#F8FAFC' }}>
                    <TableRow>
                      {['PAYER', 'ID', 'TYPE OF PROGRAM', 'AUTH NUMBER', 'CODES', 'START & END DATE', 'UNITS', 'UNITS LEFT', 'CONTACTS'].map((h, hIdx) => (
                        <TableCell
                          key={hIdx}
                          align={h === 'UNITS' || h === 'UNITS LEFT' ? 'center' : 'left'}
                          sx={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: '#475569',
                            py: 1.2,
                            px: 1.6,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {item.insurances.length > 0 ? (
                      item.insurances.map((ins, iIdx) => (
                        <TableRow key={iIdx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0B2540' }}>{ins.payer}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: 'monospace' }}>{ins.id}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: '#334155' }}>{ins.typeOfProgram}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: '#0F172A', fontWeight: 600 }}>{ins.authNumber}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {ins.codes.map((code, cIdx) => (
                                <Chip
                                  key={cIdx}
                                  label={code}
                                  size="small"
                                  sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    backgroundColor: '#EFF6FF',
                                    color: '#2563EB',
                                    height: '20px',
                                  }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem', color: '#475569' }}>{ins.startEndDate}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0F172A' }}>{ins.units}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={ins.unitsLeft}
                              size="small"
                              sx={{
                                height: '22px',
                                fontSize: '0.74rem',
                                fontWeight: 800,
                                backgroundColor: ins.unitsLeft <= 10 ? '#FEE2E2' : '#E0F2FE',
                                color: ins.unitsLeft <= 10 ? '#DC2626' : '#0284C7',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem', color: '#64748B' }}>{ins.contacts}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          align="center"
                          sx={{
                            py: 2.2,
                            fontSize: '0.8rem',
                            fontStyle: 'italic',
                            color: '#94A3B8',
                          }}
                        >
                          No active insurance profile attached for this caseload period.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Interactive Clinical Notes Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                  <EditNoteRoundedIcon sx={{ fontSize: '1.2rem', color: '#0B2540' }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#0F172A' }}>
                    Clinical & Caseload Notes
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    minHeight: '65px',
                    borderRadius: '12px',
                    border: '1px solid #E2E8F0',
                    p: 1.8,
                    backgroundColor: '#F8FAFC',
                    fontSize: '0.82rem',
                    color: item.notes ? '#334155' : '#94A3B8',
                    fontStyle: item.notes ? 'normal' : 'italic',
                    lineHeight: 1.5,
                  }}
                >
                  {item.notes || 'No active notes logged for this client profile.'}
                </Box>
              </Box>

              <Divider sx={{ my: 2.5, borderColor: '#EEF2F6' }} />

              {/* Provider & Supervisor Verification Footer */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                      Primary Clinician
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0B2540' }}>
                      {item.staffName}
                    </Typography>
                  </Box>

                  <Box sx={{ borderLeft: '1px solid #E2E8F0', pl: 3 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                      Primary Supervisor
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#2563EB' }}>
                      {item.primarySupervisor}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                    Last Supervised Date
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F172A' }}>
                    {item.dateSupervised}
                  </Typography>
                </Box>
              </Box>

            </Box>
          </Paper>
        ))}
      </Box>

    </Box>
  );
};

export default CaseLoadReport;